import React from 'react';
import { TriangleAlert } from 'lucide-react';
import { isQueueAlertOpen, type QueueAlert } from 'types/queueAlert.type';
import {
    formatAtRiskPct,
    formatCapacityPerHour,
    formatQueueDelayAlertSummary,
    formatTimeRemaining,
} from 'utils/queueDelayAlert.utils';

import styles from './QueueDelayAlertBanner.module.scss';

export interface QueueDelayAlertBannerProps {
    /** The workflow's open alert, straight off the Live Monitor poll. */
    alert?: QueueAlert | null;
}

const formatTriggeredAt = (triggeredAt: string): string | null => {
    const date = new Date(triggeredAt);
    if (Number.isNaN(date.getTime())) return null;
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
};

/**
 * Queue delay banner for the workflow detail page (FR-3.1). Renders nothing unless the
 * workflow has an open alert: the poll drops the field once the evaluator resolves it, so the
 * banner disappears on its own within one poll cycle.
 */
export const QueueDelayAlertBanner: React.FC<QueueDelayAlertBannerProps> = ({ alert }) => {
    if (!alert || !isQueueAlertOpen(alert)) return null;

    const { pendingOverdue, capacityPerHour, timeRemainingHours, atRiskPct } = alert.payload;
    const triggeredAt = formatTriggeredAt(alert.triggeredAt);

    return (
        <section className={styles.banner} role='status' data-testid='queue-delay-alert-banner'>
            <TriangleAlert className={styles.icon} size={20} aria-hidden='true' />

            <div className={styles.content}>
                <div className={styles.header}>
                    <h4 className={styles.title}>Queue delay</h4>
                    {triggeredAt ? <span className={styles.triggeredAt}>Since {triggeredAt}</span> : null}
                </div>

                <p className={styles.summary}>{formatQueueDelayAlertSummary(alert.payload)}</p>

                <dl className={styles.stats}>
                    <div className={styles.stat}>
                        <dt>Overdue</dt>
                        <dd>{pendingOverdue.toLocaleString()}</dd>
                    </div>
                    <div className={styles.stat}>
                        <dt>At risk</dt>
                        <dd>{formatAtRiskPct(atRiskPct)}</dd>
                    </div>
                    <div className={styles.stat}>
                        <dt>Projected capacity</dt>
                        <dd>{formatCapacityPerHour(capacityPerHour)}</dd>
                    </div>
                    <div className={styles.stat}>
                        <dt>Time remaining</dt>
                        <dd>{formatTimeRemaining(timeRemainingHours)}</dd>
                    </div>
                </dl>
            </div>
        </section>
    );
};

export default QueueDelayAlertBanner;
